/**
 * The org router works for the organization management for the app.
 * The org router use the root org as the module scope.
 * It use the tbs.view.Scene as the plugin to improve the development
 * base on the vue framework. It treats the following view as the scene
 * instance.
 *
 * The TBS Scene plugin introduces the properties, mediator, convertor, 
 * translator properties for the built-in meta object.
 *
 * The properties property stands for the context object with the scene view.
 * It contains the title, route, router, bridge* and so on which is used into
 * the mediator.
 *
 * The mediator property stands for the business controller with the scene
 * view. We treat the view as the special component to maintain the whole
 * component-based development pattern. Therefore, it separate the business
 * logic from view to mediator.
 *
 * The convertor property stands for the data convertor between view object
 * and entity object. We treat the data via requesting the service API as the 
 * entity object which is the persistence data and also treat the data via 
 * rendering the UI component as the view object.
 *
 * The translator property stands for the dictionary feature and also acts as the
 * simple version for the i18n module.
 */
const OrgRouter = {
    path: '/org',
    name: 'Org',
    component: () => import('@/tbs/core/Scene'),
    children: [
        {
            path: 'organization/list',
            name: 'OrgListOrganization',
            meta: {
                properties: {
                    title: '机构列表'
                },
                mediator: () => import('@/tbs/controller/OrganizationMediator'),
                convertor: () => import('@/tbs/model/vo/Organization'),
                translator:() => import('@/tbs/model/to/Organization')
            },
            component: () => import('@/views/org/OrganizationList')
        },
        {
            path: 'organization/create',
            name: 'OrgCreateOrganization',
            meta: {
                properties: {
                    title: '新建机构'
                },
                mediator: () => import('@/tbs/controller/OrganizationMediator'),
                convertor: () => import('@/tbs/model/vo/Organization'),
                translator:() => import('@/tbs/model/to/Organization')
            },
            component: () => import('@/views/org/OrganizationEdit')
        },
        {
            path: 'organization/edit/:id',
            name: 'OrgEditOrganization',
            meta: {
                properties: {
                    title: '编辑机构'
                },
                mediator: () => import('@/tbs/controller/OrganizationMediator'),
                convertor: () => import('@/tbs/model/vo/Organization'),
                translator:() => import('@/tbs/model/to/Organization')
            },
            component: () => import('@/views/org/OrganizationEdit')
        },
        {
            path: 'organization/detail/:id',
            name: 'OrgDetailOrganization',
            meta: {
                properties: {
                    title: '查看机构'
                },
                mediator: () => import('@/tbs/controller/OrganizationMediator'),
                convertor: () => import('@/tbs/model/vo/Organization'),
                translator:() => import('@/tbs/model/to/Organization')
            },
            component: () => import('@/views/org/OrganizationDetail')
        },
        {
            path: 'match-name/create',
            name: 'OrgCreateMatchName',
            meta: {
                properties: {
                    title: '新建赛事名称'
                },
                mediator: () => import('@/tbs/controller/MatchNameMediator'),
                convertor: () => import('@/tbs/model/vo/Match'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchNameEdit')
        },
        {
            path: 'match-brand/list',
            name: 'OrgListMatchBrand',
            meta: {
                properties: {
                    title: '选择品牌赛事'
                },
                mediator: () => import('@/tbs/controller/MatchBrandMediator'),
                convertor: () => import('@/tbs/model/vo/MatchBrand'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchBrandList')
        },
        {
            path: 'match-brand/create',
            name: 'OrgCreateMatchBrand',
            meta: {
                properties: {
                    title: '新建品牌赛事'
                },
                mediator: () => import('@/tbs/controller/MatchBrandMediator'),
                convertor: () => import('@/tbs/model/vo/MatchBrand'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchBrandEdit')
        },
        {
            path: 'match-brand/edit/:id',
            name: 'OrgEditMatchBrand',
            meta: {
                properties: {
                    title: '编辑品牌赛事'
                },
                mediator: () => import('@/tbs/controller/MatchBrandMediator'),
                convertor: () => import('@/tbs/model/vo/MatchBrand'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchBrandEdit')
        },
        {
            path: 'match-brand/detail/:id',
            name: 'OrgDetailMatchBrand',
            meta: {
                properties: {
                    title: '查看品牌赛事'
                },
                mediator: () => import('@/tbs/controller/MatchBrandMediator'),
                convertor: () => import('@/tbs/model/vo/MatchBrand'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchBrandDetail')
        },
        {
            path: 'match/create',
            name: 'OrgCreateMatch',
            meta: {
                properties: {
                    title: '新建赛事'
                },
                mediator: () => import('@/tbs/controller/MatchMediator'),
                convertor: () => import('@/tbs/model/vo/Match'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchEdit')
        },
        {
            path: 'match/edit/:id',
            name: 'OrgEditMatch',
            meta: {
                properties: {
                    title: '赛事基本信息'
                },
                mediator: () => import('@/tbs/controller/MatchMediator'),
                convertor: () => import('@/tbs/model/vo/Match'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchInfoEdit')
        },
        {
            path: 'organization/manage/:id',
            name: 'OrgManageOrganization',
            meta: {
                properties: {
                    title: '机构管理'
                },
                mediator: () => import('@/tbs/controller/OrganizationManagerMediator'),
                convertor: () => import('@/tbs/model/vo/OrganizationManager'),
                translator:() => import('@/tbs/model/to/OrganizationManager')
            },
            component: () => import('@/views/org/OrganizationManage')
        },
        {
            path: 'organization/manage',
            name: 'OrgSelectOrganization',
            meta: {
                properties: {
                    title: '选择机构管理',
                    login: 'app'
                },
                mediator: () => import('@/tbs/controller/OrganizationManagerMediator'),
                convertor: () => import('@/tbs/model/vo/OrganizationManager'),
                translator:() => import('@/tbs/model/to/OrganizationManager')
            },
            component: () => import('@/views/org/OrganizationManage')
        },
        {
            path: 'match/manage/:id',
            name: 'OrgManageMatch',
            meta: {
                properties: {
                    title: '赛事管理'
                },
                mediator: () => import('@/tbs/controller/MatchManagerMediator'),
                convertor: () => import('@/tbs/model/vo/MatchManager'),
                translator:() => import('@/tbs/model/to/MatchManager')
            },
            component: () => import('@/views/org/MatchManage')
        },
        {
            path: 'match-rule/edit/:id',
            name: 'OrgEditMatchRule',
            meta: {
                properties: {
                    title: '赛事规则'
                },
                mediator: () => import('@/tbs/controller/MatchRuleMediator'),
                convertor: () => import('@/tbs/model/vo/MatchRule'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchRuleEdit')
        },
        {
            path: 'match/:id/team/list',
            name: 'OrgListMatchTeam',
            meta: {
                properties: {
                    title: '赛事球队'
                },
                mediator: () => import('@/tbs/controller/MatchTeamMediator'),
                convertor: () => import('@/tbs/model/vo/MatchTeam'),
                translator:() => import('@/tbs/model/to/Team')
            },
            component: () => import('@/views/org/MatchTeamList')
        },
        {
            path: 'team/create',
            name: 'OrgCreateTeam',
            meta: {
                properties: {
                    title: '新建球队'
                },
                mediator: () => import('@/tbs/controller/MatchTeamMediator'),
                convertor: () => import('@/tbs/model/vo/MatchTeam'),
                translator:() => import('@/tbs/model/to/Team')
            },
            component: () => import('@/views/org/TeamEdit')
        },
        {
            path: 'team/edit/:id',
            name: 'OrgEditTeam',
            meta: {
                properties: {
                    title: '编辑球队'
                },
                mediator: () => import('@/tbs/controller/MatchTeamMediator'),
                convertor: () => import('@/tbs/model/vo/MatchTeam'),
                translator:() => import('@/tbs/model/to/Team')
            },
            component: () => import('@/views/org/TeamEdit')
        },
        {
            path: 'team/detail/:id',
            name: 'OrgDetailTeam',
            meta: {
                properties: {
                    title: '球队基本信息'
                },
                mediator: () => import('@/tbs/controller/MatchTeamMediator'),
                convertor: () => import('@/tbs/model/vo/MatchTeam'),
                translator:() => import('@/tbs/model/to/Team')
            },
            component: () => import('@/views/org/TeamDetail')
        },
        {
            path: 'team/manage/:id',
            name: 'OrgManageTeam',
            meta: {
                properties: {
                    title: '球队管理'
                },
                mediator: () => import('@/tbs/controller/TeamManagerMediator'),
                convertor: () => import('@/tbs/model/vo/TeamManager'),
                translator:() => import('@/tbs/model/to/Team')
            },
            component: () => import('@/views/org/TeamManage')
        },
        {
            path: 'player/batch',
            name: 'OrgBatchPlayer',
            meta: {
                properties: {
                    title: '批量新建球员'
                },
                mediator: () => import('@/tbs/controller/PlayerMediator'),
                convertor: () => import('@/tbs/model/vo/Player'),
                translator:() => import('@/tbs/model/to/Player')
            },
            component: () => import('@/views/org/PlayerBatch')
        },
        {
            path: 'player/create',
            name: 'OrgCreatePlayer',
            meta: {
                properties: {
                    title: '新建球员'
                },
                mediator: () => import('@/tbs/controller/PlayerMediator'),
                convertor: () => import('@/tbs/model/vo/Player'),
                translator:() => import('@/tbs/model/to/Player')
            },
            component: () => import('@/views/org/PlayerEdit')
        },
        {
            path: 'player/edit/:id',
            name: 'OrgEditPlayer',
            meta: {
                properties: {
                    title: '编辑球员'
                },
                mediator: () => import('@/tbs/controller/PlayerMediator'),
                convertor: () => import('@/tbs/model/vo/Player'),
                translator:() => import('@/tbs/model/to/Player')
            },
            component: () => import('@/views/org/PlayerEdit')
        },
        {
            path: 'player/detail/:id',
            name: 'OrgDetailPlayer',
            meta: {
                properties: {
                    title: '查看球员'
                },
                mediator: () => import('@/tbs/controller/PlayerMediator'),
                convertor: () => import('@/tbs/model/vo/Player'),
                translator:() => import('@/tbs/model/to/Player')
            },
            component: () => import('@/views/org/PlayerDetail')
        },
        {
            path: 'match/:id/schedule/manage',
            name: 'OrgManageMatchSchedule',
            meta: {
                properties: {
                    title: '赛程管理'
                },
                mediator: () => import('@/tbs/controller/MatchScheduleManagerMediator'),
                convertor: () => import('@/tbs/model/vo/MatchScheduleManager'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchScheduleManage')
        },
        {
            path: 'game/create',
            name: 'OrgCreateGame',
            meta: {
                properties: {
                    title: '新建比赛'
                },
                mediator: () => import('@/tbs/controller/GameMediator'),
                convertor: () => import('@/tbs/model/vo/Game'),
                translator:() => import('@/tbs/model/to/Game')
            },
            component: () => import('@/views/org/GameEdit')
        },
        {
            path: 'game/manage/:id',
            name: 'OrgManageGame',
            meta: {
                properties: {
                    title: '比赛详情'
                },
                mediator: () => import('@/tbs/controller/GameManagerMediator'),
                convertor: () => import('@/tbs/model/vo/Game'),
                translator:() => import('@/tbs/model/to/Game')
            },
            component: () => import('@/views/org/GameManage')
        },
        {
            path: 'game/:gameId/stats/create',
            name: 'OrgCreateGameStats',
            meta: {
                properties: {
                    title: '添加统计'
                },
                mediator: () => import('@/tbs/controller/GameStatsMediator'),
                convertor: () => import('@/tbs/model/vo/GameStats'),
                translator:() => import('@/tbs/model/to/Game')
            },
            component: () => import('@/views/org/GameStatsEdit')
        },
        {
            path: 'game/:gameId/stats/edit/:id',
            name: 'OrgEditGameStats',
            meta: {
                properties: {
                    title: '编辑统计'
                },
                mediator: () => import('@/tbs/controller/GameStatsMediator'),
                convertor: () => import('@/tbs/model/vo/GameStats'),
                translator:() => import('@/tbs/model/to/Game')
            },
            component: () => import('@/views/org/GameStatsEdit')
        },
        {
            path: 'game/:gameId/event/create',
            name: 'OrgCreateGameEvent',
            meta: {
                properties: {
                    title: '添加事件'
                },
                mediator: () => import('@/tbs/controller/GameEventMediator'),
                convertor: () => import('@/tbs/model/vo/GameEvent'),
                translator:() => import('@/tbs/model/to/Game')
            },
            component: () => import('@/views/org/GameEventEdit'),
        },
        {
            path: 'game/:gameId/event/edit/:id',
            name: 'OrgEditGameEvent',
            meta: {
                properties: {
                    title: '编辑事件'
                },
                mediator: () => import('@/tbs/controller/GameEventMediator'),
                convertor: () => import('@/tbs/model/vo/GameEvent'),
                translator:() => import('@/tbs/model/to/Game')
            },
            component: () => import('@/views/org/GameEventEdit')
        },
        {
            path: 'staff/list',
            name: 'OrgListStaff',
            meta: {
                properties: {
                    title: '工作人员'
                },
                mediator: () => import('@/tbs/controller/StaffMediator'),
                convertor: () => import('@/tbs/model/vo/Staff'),
                translator:() => import('@/tbs/model/to/Staff')
            },
            component: () => import('@/views/org/StaffList')
        },
        {
            path: 'staff/create',
            name: 'OrgCreateStaff',
            meta: {
                properties: {
                    title: '新建工作人员'
                },
                mediator: () => import('@/tbs/controller/StaffMediator'),
                convertor: () => import('@/tbs/model/vo/Staff'),
                translator:() => import('@/tbs/model/to/Staff')
            },
            component: () => import('@/views/org/StaffEdit')
        },
        {
            path: 'staff/edit/:id',
            name: 'OrgEditStaff',
            meta: {
                properties: {
                    title: '编辑工作人员'
                },
                mediator: () => import('@/tbs/controller/StaffMediator'),
                convertor: () => import('@/tbs/model/vo/Staff'),
                translator:() => import('@/tbs/model/to/Staff')
            },
            component: () => import('@/views/org/StaffEdit')
        },
        {
            path: 'staff/detail/:id',
            name: 'OrgDetailStaff',
            meta: {
                properties: {
                    title: '查看工作人员'
                },
                mediator: () => import('@/tbs/controller/StaffMediator'),
                convertor: () => import('@/tbs/model/vo/Staff'),
                translator:() => import('@/tbs/model/to/Staff')
            },
            component: () => import('@/views/org/StaffDetail')
        },
        {
            path: 'match/:id/staff/list',
            name: 'OrgListMatchStaff',
            meta: {
                properties: {
                    title: '赛事工作组'
                },
                mediator: () => import('@/tbs/controller/MatchStaffMediator'),
                convertor: () => import('@/tbs/model/vo/StaffInMatch'),
                translator:() => import('@/tbs/model/to/Staff')
            },
            component: () => import('@/views/org/MatchStaffList')
        },
        {
            path: 'match-suspension-reminder/edit/:id',
            name: 'OrgEditMatchSuspensionReminder',
            meta: {
                properties: {
                    title: '停赛提醒'
                },
                mediator: () => import('@/tbs/controller/MatchSuspensionReminderMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSuspensionReminder'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchSuspensionReminderEdit')
        },
        {
            path: 'match/:id/suspension-report/list',
            name: 'OrgListMatchReport',
            meta: {
                properties: {
                    title: '红黄牌停赛'
                },
                mediator: () => import('@/tbs/controller/MatchSuspensionReportMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSuspensionReport'),
                translator:() => import('@/tbs/model/to/Match')
            },
            component: () => import('@/views/org/MatchSuspensionReportList')
        },
        {
            path: 'staff/invite',
            name: 'OrgInviteStaff',
            meta: {
                properties: {
                    title: '工作人员邀请'
                },
                mediator: () => import('@/tbs/controller/StaffInviteMediator'),
                convertor: () => import('@/tbs/model/vo/Staff'),
                translator:() => import('@/tbs/model/to/Staff')
            },
            component: () => import('@/views/org/StaffInvite')
        },
        {
            path: 'staff/accept',
            name: 'OrgAcceptStaff',
            meta: {
                properties: {
                    title: ''
                },
                mediator: () => import('@/tbs/controller/StaffInviteMediator'),
                convertor: () => import('@/tbs/model/vo/Staff'),
                translator:() => import('@/tbs/model/to/Staff')
            },
            component: () => import('@/views/org/StaffAccept')
        },
        {
            path: 'match/entry',
            name: 'OrgEntryMatch',
            meta: {
                properties: {
                    title: '赛事入驻',
                    hideNavBar: true,
                    login: 'app'
                },
                mediator: () => import('@/tbs/controller/MatchEntryMediator'),
                convertor: () => import('@/tbs/model/vo/MatchEntry'),
                translator:() => import('@/tbs/model/to/MatchEntry')
            },
            component: () => import('@/views/org/MatchEntry')
        },
        {
            path: 'match/:id/signup-payment/manage',
            name: 'OrgManageMatchSignupPayment',
            meta: {
                properties: {
                    title: '报名缴费'
                },
                mediator: () => import('@/tbs/controller/MatchSignupPaymentMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSignupPayment'),
                translator:() => import('@/tbs/model/to/MatchSignupPayment')
            },
            component: () => import('@/views/org/MatchSignupPaymentManage')
        },
        {
            path: 'match/:id/signup-config/detail',
            name: 'OrgDetailMatchSignupConfig',
            meta: {
                properties: {
                    title: '报名资料设置'
                },
                mediator: () => import('@/tbs/controller/MatchSignupConfigMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSignupConfig'),
                translator:() => import('@/tbs/model/to/MatchSignupPayment')
            },
            component: () => import('@/views/org/MatchSignupConfigDetail')
        },
        {
            path: 'signup-team-config/edit/:id',
            name: 'OrgEditMatchSignupTeamConfig',
            meta: {
                properties: {
                    title: '队伍资料设置'
                },
                mediator: () => import('@/tbs/controller/MatchSignupConfigMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSignupTeamConfig'),
                translator:() => import('@/tbs/model/to/MatchSignupPayment')
            },
            component: () => import('@/views/org/MatchSignupTeamConfigEdit')
        },
        {
            path: 'signup-member-config/edit/:id',
            name: 'OrgEditMatchSignupMemberConfig',
            meta: {
                properties: {
                    title: '队员资料设置'
                },
                mediator: () => import('@/tbs/controller/MatchSignupConfigMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSignupMemberConfig'),
                translator:() => import('@/tbs/model/to/MatchSignupPayment')
            },
            component: () => import('@/views/org/MatchSignupMemberConfigEdit')
        },
        {
            path: 'signup-contestant-config/edit/:id',
            name: 'OrgEditMatchSignupContestantConfig',
            meta: {
                properties: {
                    title: '参赛队员限制设置'
                },
                mediator: () => import('@/tbs/controller/MatchSignupConfigMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSignupContestantConfig'),
                translator:() => import('@/tbs/model/to/MatchSignupPayment')
            },
            component: () => import('@/views/org/MatchSignupContestantConfigEdit')
        },
        {
            path: 'signup-staff-config/edit/:id',
            name: 'OrgEditMatchSignupStaffConfig',
            meta: {
                properties: {
                    title: '工作人员报名设置'
                },
                mediator: () => import('@/tbs/controller/MatchSignupConfigMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSignupStaffConfig'),
                translator:() => import('@/tbs/model/to/MatchSignupPayment')
            },
            component: () => import('@/views/org/MatchSignupStaffConfigEdit')
        },
        {
            path: 'match/:id/signup-payment/create',
            name: 'OrgCreateMatchSignupPayment',
            meta: {
                properties: {
                    title: '新建在线报名表',
                    cached: 'matchSignup,imageList'
                },
                mediator: () => import('@/tbs/controller/MatchSignupPaymentMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSignupPayment'),
                translator:() => import('@/tbs/model/to/MatchSignupPayment')
            },
            component: () => import('@/views/org/MatchSignupPaymentEdit')
        },
        {
            path: 'signup-payment/edit/:id',
            name: 'OrgEditMatchSignupPayment',
            meta: {
                properties: {
                    title: '编辑在线报名表',
                    cached: 'matchSignup,imageList'
                },
                mediator: () => import('@/tbs/controller/MatchSignupPaymentMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSignupPayment'),
                translator:() => import('@/tbs/model/to/MatchSignupPayment')
            },
            component: () => import('@/views/org/MatchSignupPaymentEdit')
        },
        {
            path: 'signup-payment/detail/:id',
            name: 'OrgDetailMatchSignupPayment',
            meta: {
                properties: {
                    title: '报名信息'
                },
                mediator: () => import('@/tbs/controller/MatchSignupPaymentDetailMediator'),
                convertor: () => import('@/tbs/model/vo/MatchSignupPayment'),
                translator:() => import('@/tbs/model/to/MatchSignupPayment')
            },
            component: () => import('@/views/org/MatchSignupPaymentDetail')
        },
        {
            path: 'referee/list',
            name: 'OrgListReferee',
            meta: {
                properties: {
                    title: '裁判库'
                },
                mediator: () => import('@/tbs/controller/RefereeMediator'),
                convertor: () => import('@/tbs/model/vo/Referee'),
                translator:() => import('@/tbs/model/to/Referee')
            },
            component: () => import('@/views/org/RefereeList')
        },
        {
            path: 'referee/create',
            name: 'OrgCreateReferee',
            meta: {
                properties: {
                    title: '新建裁判员'
                },
                mediator: () => import('@/tbs/controller/RefereeMediator'),
                convertor: () => import('@/tbs/model/vo/Referee'),
                translator:() => import('@/tbs/model/to/Referee')
            },
            component: () => import('@/views/org/RefereeEdit')
        },
        {
            path: 'referee/edit/:id',
            name: 'OrgEditReferee',
            meta: {
                properties: {
                    title: '编辑裁判员'
                },
                mediator: () => import('@/tbs/controller/RefereeMediator'),
                convertor: () => import('@/tbs/model/vo/Referee'),
                translator:() => import('@/tbs/model/to/Referee')
            },
            component: () => import('@/views/org/RefereeEdit')
        },
        {
            path: 'referee/detail/:id',
            name: 'OrgDetailReferee',
            meta: {
                properties: {
                    title: '查看裁判员'
                },
                mediator: () => import('@/tbs/controller/RefereeMediator'),
                convertor: () => import('@/tbs/model/vo/Referee'),
                translator:() => import('@/tbs/model/to/Referee')
            },
            component: () => import('@/views/org/RefereeDetail')
        },
        {
            path: 'match/:id/referee/list',
            name: 'OrgListMatchReferee',
            meta: {
                properties: {
                    title: '赛事裁判组'
                },
                mediator: () => import('@/tbs/controller/MatchRefereeMediator'),
                convertor: () => import('@/tbs/model/vo/RefereeInMatch'),
                translator:() => import('@/tbs/model/to/Referee')
            },
            component: () => import('@/views/org/MatchRefereeList')
        },
        {
            path: 'match/:id/referee/create',
            name: 'OrgCreateMatchReferee',
            meta: {
                properties: {
                    title: '新建裁判员'
                },
                mediator: () => import('@/tbs/controller/MatchRefereeMediator'),
                convertor: () => import('@/tbs/model/vo/RefereeInMatch'),
                translator:() => import('@/tbs/model/to/Referee')
            },
            component: () => import('@/views/org/RefereeEdit')
        },
        {
            path: 'referee/invite/:id',
            name: 'OrgInviteReferee',
            meta: {
                properties: {
                    title: '裁判员邀请'
                },
                mediator: () => import('@/tbs/controller/RefereeInviteMediator'),
                convertor: () => import('@/tbs/model/vo/Referee'),
                translator:() => import('@/tbs/model/to/Referee')
            },
            component: () => import('@/views/org/MemberInvite')
        },
        {
            path: 'referee/accept',
            name: 'OrgAcceptReferee',
            meta: {
                properties: {
                    title: ''
                },
                mediator: () => import('@/tbs/controller/RefereeInviteMediator'),
                convertor: () => import('@/tbs/model/vo/Referee'),
                translator:() => import('@/tbs/model/to/Referee')
            },
            component: () => import('@/views/org/MemberAccept')
        },
        {
            path: 'group/edit/:id',
            name: 'OrgEditGroup',
            meta: {
                properties: {
                    title: 'groupName球队列表',
                    query: 'groupName'
                },
                mediator: () => import('@/tbs/controller/GroupMediator'),
                convertor: () => import('@/tbs/model/vo/Group'),
                translator:() => import('@/tbs/model/to/Team')
            },
            component: () => import('@/views/org/GroupEdit')
        },
    ]
}

export default OrgRouter